import { useState } from "react";
import { Link, Outlet, useMatch, useParams } from "react-router";

import { QueryErrorState } from "@/api/QueryErrorState";
import { QueryPendingState } from "@/api/QueryPendingState";
import { queryKeys, useProjectOverviewQuery } from "@/api/hooks";
import { isTerminalQueryError } from "@/api/queryClient";
import { AppErrorBoundary } from "@/app/AppErrorBoundary";
import { useCapability } from "@/auth/SessionContext";
import { formatDate } from "@/lib/format";
import { labPath } from "@/lib/labUrl";
import { describeProjectSettings } from "@/lib/projectSettings";
import { PageHeader, type Breadcrumb } from "@/shell/PageHeader";
import {
  CLINICAL_CONFIG_SECTIONS,
  CLINICAL_DEFAULT_SLUG,
  findSection,
} from "@/shell/navModel";
import { cn } from "@/lib/cn";
import { Button } from "@/ui/Button";
import { Notice } from "@/ui/Notice";
import { Skeleton } from "@/ui/Skeleton";
import { Tooltip } from "@/ui/Tooltip";
import { ProjectSettingsDialog } from "@/ui/project/ProjectSettingsDialog";
import { FinalQuestioningWarning } from "@/ui/project/FinalQuestioningWarning";
import {
  ProjectLifecycleDialog,
  type ProjectLifecycleMode,
} from "@/ui/project/ProjectLifecycleDialog";

/**
 * `projectId` is an opaque, server-issued identifier. ADR 0001 forbids patient
 * and candidate identifiers in any browser URL; src/invariants asserts that no
 * route in this application declares one.
 */
export function useProjectId(): string {
  const { projectId } = useParams();
  return projectId ?? "";
}

/**
 * Which Project surface the address resolved to.
 *
 * Computed once and shared by the tabs and the breadcrumbs, so the two can
 * never disagree about where a clinician is.
 *
 * `"other"` covers anything below a Project this layout does not recognise. It
 * used to have a real occupant — the legacy `product-settings` catches, which
 * rendered a removed state inside this frame — and #516 made those addresses
 * redirects registered outside the layout, so nothing routable reaches it now.
 * It stays as the fallback rather than being narrowed away: a surface that
 * arrives without a branch here must not silently be described as clinical
 * configuration, which is what removing the default would produce.
 */
type ProjectSurface =
  "clinical" | "overview" | "previews" | "create-from" | "other";

function useProjectSurface(): ProjectSurface {
  // Active state is computed rather than delegated to NavLink's `end`, because
  // the three tabs answer at different address SHAPES: the Overview is the
  // project root (`/projects/:id`, where it is the index), the clinical surface
  // is `/projects/:id/config/**`, and Preview History is `/previews`. `end:
  // false` on the Overview would light it up on every address below the project,
  // including the other two and `preview`; `end: true` on the clinical tab
  // would drop it on section addresses. Neither is true.
  //
  // It is computed with the router's own matcher against the route table's
  // patterns, not by comparing `location.pathname` as a string. The tab is the
  // clinician's only orientation for which surface answered, so it has to be
  // right on every spelling React Router accepts, and it accepts more than one:
  // a trailing slash (`/projects/:id/`) and any casing of a static segment
  // (`/projects/:id/CONFIG/bg-sets`) both route the same content and both
  // survive into `location.pathname`. `matchPath` already ignores the first and is
  // case-insensitive unless a route opts in — none here does — so deferring to
  // it makes the tab agree with the route that actually rendered, by
  // construction, rather than by a comparison that has to be kept in step with
  // the route table by hand.
  //
  // `config/*` covers `config` and `config/:section` in one pattern: a splat
  // matches zero or more trailing segments.
  const atProjectRoot = useMatch("/projects/:projectId") !== null;
  const atConfig = useMatch("/projects/:projectId/config/*") !== null;
  // #518: `/preview` and `/submit` are route-backed MODALS over the Overview,
  // so the surface that answered at those addresses IS the Overview — the tab
  // stays lit and the trail stays the project's, exactly as they render
  // beneath the dialog. The old full-page `"preview"` surface left with the
  // page itself.
  const atPreviewModal = useMatch("/projects/:projectId/preview") !== null;
  const atSubmitModal = useMatch("/projects/:projectId/submit") !== null;
  const atOverviewModal = atPreviewModal || atSubmitModal;
  const atPreviews = useMatch("/projects/:projectId/previews") !== null;
  const atCreateFrom = useMatch("/projects/:projectId/create-from") !== null;

  if (atCreateFrom) return "create-from";
  if (atPreviews) return "previews";
  if (atConfig) return "clinical";
  // #516: the project root IS the Overview. It was `"clinical"` here until this
  // ticket, because the index rendered `<ProjectClinicalConfigRoute landing />`.
  if (atProjectRoot || atOverviewModal) return "overview";
  return "other";
}

function ProjectTabs({
  projectId,
  surface,
}: {
  projectId: string;
  surface: ProjectSurface;
}) {
  const base = `/projects/${encodeURIComponent(projectId)}`;

  // #153: no Product settings tab — the Clinician Editor authors clinical-pack
  // content only; presentation and policy settings are Tenant Manifest domain.
  //
  // #1138 adds the first-class durable Preview-discovery surface after the
  // two authoring tabs: Overview → Clinical configuration → Previews.
  //
  // This SUPERSEDES #203's "clinical content is FIRST" for the tab ORDER, and
  // says so rather than quietly reversing it. #203 (2026-08-04) ordered the tabs
  // so a Project stopped reading as a deployment console; that concern is now
  // carried by the Overview's own composition and by the publication surfaces
  // `src/invariants/projectUxAmendment.test.ts` still bans outright. What #489
  // aligns to is the ratified product reference, which renders `Overview` then
  // `Clinical configuration`; #1138 adds `Previews` after both.
  //
  // The two tabs that LEFT are not deleted surfaces:
  //  * `Adopted content` (#372) — adoption is not a mode of its own. The action
  //    now stands beside the evidence, inside every clinical section's
  //    `SectionAdoptionBlock`. #516 then deleted the addresses themselves.
  //  * `Preview as a patient` (#260) — a preview is an ACTION on the project a
  //    clinician just reviewed, offered from the Overview exactly as the
  //    reference places it. Since #518 the `/preview` address answers with the
  //    canonical modal over the Overview rather than a page of its own, and
  //    the journey's Next-step card ("Open real chat") opens it.
  //
  // #516 RESOLVES the divergence the note here used to defer to #448: the
  // Overview's href is the project ROOT, because the root is where the Overview
  // now renders and `/projects/:id/overview` no longer exists. #448 was scoped
  // to annotating the reference spec and never covered this application, so the
  // deferral had no owner; this ticket is the owner.
  const tabs = [
    {
      label: "Overview",
      to: base,
      isActive: surface === "overview",
    },
    {
      label: "Clinical configuration",
      to: `${base}/config/bg-sets`,
      isActive: surface === "clinical",
    },
    {
      label: "Previews",
      to: `${base}/previews`,
      isActive: surface === "previews",
    },
    // The Lab is its own full-screen route, so this is a plain link.
    {
      label: "Reliv Labs",
      to: labPath(projectId),
      isActive: false,
    },
  ];

  return (
    <nav aria-label="Project sections" className="border-b border-border">
      <ul className="flex flex-wrap gap-6">
        {tabs.map((tab) => (
          <li key={tab.label}>
            <Link
              to={tab.to}
              aria-current={tab.isActive ? "page" : undefined}
              className={cn(
                "-mb-px block border-b-2 px-1 pb-3 text-sm transition-colors",
                tab.isActive
                  ? "border-foreground font-semibold text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {tab.label}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/**
 * The trail from Projects down to where the clinician actually is (#489).
 *
 * `Projects / <project> / <surface> / <section>` wherever a clinical section is
 * being rendered, and `Projects / <project>` on the Overview, which has no
 * location below itself.
 *
 * THE PROJECT ROOT GETS THE TWO-LEVEL TRAIL since #516, because the root IS the
 * Overview. It used to get the full four-level trail: `/projects/:id` rendered
 * the clinical surface in place with its default section (#203), `SectionRail`
 * marked that section current there, and a two-level trail beside a rail
 * claiming BG sets is current would have been the header and the rail
 * disagreeing about one screen. Nothing renders a rail at the root any more, so
 * the trail names the project and stops.
 *
 * `"other"` keeps the two-level trail. Nothing routable reaches it today — the
 * legacy `product-settings` catches that used to are registered outside this
 * layout and redirect — but naming a surface for an address the layout does not
 * recognise would assert one exists.
 */
function projectBreadcrumbs({
  projectId,
  projectName,
  surface,
  section,
  filesEditor,
}: {
  projectId: string;
  projectName: string;
  surface: ProjectSurface;
  /** The pack files editor, a sub-view of Clinical configuration. */
  filesEditor: boolean;
  /** The addressed section, or `undefined` outside a section address. */
  section: string | undefined;
}): Breadcrumb[] {
  const base = `/projects/${encodeURIComponent(projectId)}`;
  const trail: Breadcrumb[] = [
    { label: "Projects", to: "/projects" },
    // #516: the project's own address, which is the Overview. It was
    // `${base}/overview` until this ticket deleted that address.
    { label: projectName, to: base },
  ];

  if (surface === "clinical") {
    trail.push({
      label: "Clinical configuration",
      to: `${base}/config/bg-sets`,
    });
    // The rendered section, which at the bare `config` address is the default
    // this surface redirects to rather than anything the address spells. An
    // UNKNOWN slug contributes no crumb: the body renders "Unknown section",
    // and a trail naming it would dress a wrong address up as a real location.
    const match = filesEditor
      ? undefined
      : findSection(CLINICAL_CONFIG_SECTIONS, section ?? CLINICAL_DEFAULT_SLUG);
    if (filesEditor) trail.push({ label: "Files editor" });
    if (match) {
      trail.push({ label: match.label, to: `${base}/config/${match.slug}` });
    }
  } else if (surface === "previews") {
    trail.push({ label: "Previews" });
  }
  // No crumb for `/preview` or `/submit`: those addresses answer with a MODAL
  // over the Overview (#518), and the trail beneath a dialog naming a place
  // below the Overview would disagree with the page that is actually there.
  // `create-from` keeps the two-level trail for the canonical reason — the
  // canonical authority renders it under the project's own header with no
  // deeper crumb.

  // The LAST crumb is where the clinician is, so it loses its link and gains
  // `aria-current="page"` from `PageHeader`. Stripped here rather than omitted
  // above so every branch gets the rule without restating it — a leaf that kept
  // its `to` would be a self-link announced as an ordinary destination.
  const leaf = trail[trail.length - 1];
  if (leaf) {
    trail[trail.length - 1] = { label: leaf.label };
  }

  return trail;
}

export function ProjectLayoutRoute() {
  const projectId = useProjectId();
  const surface = useProjectSurface();
  const { section } = useParams();
  const atFilesEditor = useMatch("/projects/:projectId/config/files") !== null;
  const overview = useProjectOverviewQuery(projectId);
  const archiveProject = useCapability("project.archive");
  const restoreProject = useCapability("project.restore");
  const [lifecycle, setLifecycle] = useState<ProjectLifecycleMode | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // A transient failed refresh keeps the last project frame: the layout and
  // every section beneath it read this one query, so blanking the frame would
  // also take the sections' stale handling down with it. Terminal statuses fail
  // closed even with cached data because access or endpoint availability has
  // definitively changed.
  if (
    overview.isError &&
    (!overview.data || isTerminalQueryError(overview.error))
  ) {
    return (
      <>
        <PageHeader
          breadcrumbs={[{ label: "Projects", to: "/projects" }]}
          title="Project"
        />
        <QueryErrorState
          error={overview.error}
          subject="Projects"
          title="Project could not be loaded"
          deniedDescription="This project is not available to your account in this environment."
          // A project that is absent and a project that is not yours must look
          // identical, or the 404 becomes an existence oracle.
          treatNotFoundAsDenied
          onRetry={() => void overview.refetch()}
        />
      </>
    );
  }

  const project = overview.data?.project;

  /*
    The workspace-level lifecycle action (#440): direction from the row's own
    archive facts. Active Projects keep a disabled-with-reason Archive action
    when capability is absent; archived Projects expose Restore only to a
    caller who actually holds the admin/operations capability, because #440's
    boundary says ordinary clinicians/project editors cannot see it.
    `ProjectsIndexRoute`'s module comment carries the full argument for why
    there is no per-project eligibility verdict to consult here.
  */
  const archived = project?.archive?.archived === true;
  const lifecycleMode: ProjectLifecycleMode = archived ? "restore" : "archive";
  const lifecycleCapability = archived ? restoreProject : archiveProject;
  const lifecycleRefusal =
    project && project.archive?.archiveRevision === undefined
      ? "The Editor Service did not report this project's archive revision. Reload before changing its archive state."
      : lifecycleCapability.allowed
        ? null
        : lifecycleCapability.reason;
  const lifecycleLabel = archived ? "Restore project" : "Archive project";
  const showLifecycleAction = !archived || restoreProject.allowed;

  return (
    <>
      {project && lifecycle ? (
        <ProjectLifecycleDialog
          project={project}
          mode={lifecycle}
          onClose={() => setLifecycle(null)}
        />
      ) : null}
      {settingsOpen ? (
        <ProjectSettingsDialog
          projectId={projectId}
          onClose={() => setSettingsOpen(false)}
        />
      ) : null}
      <PageHeader
        breadcrumbs={projectBreadcrumbs({
          projectId,
          projectName: project?.name ?? "Loading",
          surface,
          section,
          filesEditor: atFilesEditor,
        })}
        title={project?.name ?? " "}
        actions={
          project && showLifecycleAction ? (
            // The journey's Next-step card is the one primary action; the
            // header keeps only the secondary archive/restore control.
            <Tooltip content={lifecycleRefusal ?? lifecycleLabel}>
              <Button
                variant="ghost"
                disabled={lifecycleRefusal !== null}
                onClick={() => {
                  if (lifecycleRefusal === null) {
                    setLifecycle(lifecycleMode);
                  }
                }}
              >
                {lifecycleLabel}
              </Button>
            </Tooltip>
          ) : null
        }
        meta={
          overview.isPending ? (
            // A paused query has nothing in flight, so a shimmer here would
            // outlast the connection it is waiting for. The body below carries
            // the explanation and the retry (#215).
            overview.isPaused ? null : (
              <Skeleton className="h-4 w-40" />
            )
          ) : (
            // The Live/Draft-only badge left with #202 PR B2. It read
            // `project.lifecycleState`, a server-side inference from
            // `lastPromotedAt` that #29's AC "No singular live/promotion state
            // is inferred" retires: the Editor is not the authority for what is
            // deployed. The creation date is a fact about this Project and
            // stays.
            <span>
              {describeProjectSettings({
                tenantPackImport: project?.tenantPackImport,
                lineage: project?.lineage,
              }).summary}
              {" · "}Preview on {project?.previewTenantId}
              {" · "}Created {formatDate(overview.data.createdAt)}
              {" · "}
              <button
                type="button"
                className="underline underline-offset-2 hover:text-foreground"
                onClick={() => setSettingsOpen(true)}
              >
                Details
              </button>
            </span>
          )
        }
      />

      {/*
        The archived state, above every Project surface (#440): a deep link
        into an archived Project must say so wherever it lands, not only on
        the Overview. `Notice` keeps the live region mounted and toggles
        visibility, so assistive technology observes the state arriving. The
        copy states the FACTS the service enforces — hidden from the active
        list, everything retained, restore is an administrator action — and
        deliberately does not claim edits are refused: per-control refusals
        are the server's capability contract (#650), and a banner promising
        an enforcement the service does not yet make would be the fabricated
        state this application forbids.
      */}
      <Notice visible={archived} className="mt-4">
        This project is archived. It is hidden from the active project list and
        retained in full — every candidate, publication and evidence record
        stays exactly as it was. Restoring it is an administrator action.
      </Notice>

      {/*
        THE PROJECT HEADER's copy of §8's persistent safety warning (#853).

        Here, beside the archived notice, for the same reason that one is here:
        a deep link into a project must state this wherever it lands, not only
        on the surface that owns the control. This frame renders above the
        Overview, every clinical section AND the route-backed preview/submit
        modals, so the warning survives navigation and reload by construction
        rather than by four surfaces remembering to render it.

        It reads the same cache entry the Safety, validation and preview copies
        do, so the four can never disagree about the posture.
      */}
      <FinalQuestioningWarning
        projectId={projectId}
        surface="header"
        className="mt-4"
      />

      <ProjectTabs projectId={projectId} surface={surface} />

      <div className="pt-6">
        <AppErrorBoundary boundary="project-section">
          {overview.isPending ? (
            <QueryPendingState
              isPaused={overview.isPaused}
              failureReason={overview.failureReason}
              label="Loading project"
              // The heading the branch at the top of this component renders for
              // the same query once its retries are spent (#327). This layout
              // owns the project frame, so a failure here is the project's,
              // even though the section beneath it renders its own.
              errorTitle="Project could not be loaded"
              rows={3}
              queryKey={queryKeys.projectOverview(projectId)}
            />
          ) : (
            <Outlet />
          )}
        </AppErrorBoundary>
      </div>
    </>
  );
}
